import React, {useContext, useState, useEffect, useRef} from 'react';
import { AppContext } from '../context/AppContext';
import styled from 'styled-components';
import { nextLesson } from '../util/NextLessonUtil';
import LessonHeader from './LessonHeader';
import LessonTabs from './LessonTabs';
import axios from 'axios';
import { useHistory, useParams } from 'react-router-dom';
import { Loader } from './Loader';
import useScrollToTop from '../hooks/useScrollToTop';
import CourseNavigation from './CourseNavigation';
import {useOnScreen} from '../hooks/useOnScreen';
import Confetti from 'react-dom-confetti'

export default function Lesson() {
  const {appState, appDispatch} = useContext(AppContext);
  const [isCompleted, setIsCompleted] = useState(false);
  const [isNextLoading, setIsNextLoading] = useState(false);
  const [playerHeight, setPlayerHeight] = useState(0);
  const [isPlayerVisible, setIsPlayerVisible] = useState(true);
  const {courseKey, unitKey, lessonKey} = useParams();
  const [lesson, setLesson] = useState(null);
  const [unit, setUnit] = useState(null);
  const [next, setNext] = useState(null);
  const [isCheckingNext, setIsCheckingNext] = useState(true);
  const history = useHistory();
  const videoRef = useRef(null);
  useScrollToTop();
  const [wrapperRef, dimensions, isScrolledPast] = useOnScreen({
    liveMeasure: true,
    scrollToOffset: 0,
    scrollPastOffset: 50
  });

  const confettiConfig = {
    angle: 90,
    spread: 81,
    startVelocity: 100,
    elementCount: 175,
    dragFriction: 0.1,
    duration: 3000,
    stagger: 0,
    width: "6px",
    height: "6px",
    perspective: "900px",
    colors: ["#a864fd", "#29cdff", "#78ff44", "#ff718d", "#fdff6a"]
  }

  useEffect(() => {
    let lessonFound = false;
    appState.course.units.forEach(unit => {
      if (unit.key === unitKey) {
        setUnit(unit);
        unit.lessons.forEach(lesson => {
          if(lesson.slug === lessonKey) {
            setLesson(lesson);
            lessonFound = true;
          }
        });
      }
    });
    if(!lessonFound) {
      history.push(`/courses/${courseKey}/error`);
    } else {
      setNext(nextLesson(unitKey, lessonKey, appState.course.units))
      setIsCheckingNext(false)
    }
  }, [courseKey, unitKey, lessonKey, appState.course.units, history]);

  useEffect(() => {
    if(isScrolledPast) {
      setIsPlayerVisible(false);
    } else {
      setIsPlayerVisible(true);
    }
    if(isPlayerVisible && dimensions && dimensions.height) {
      setPlayerHeight(dimensions.height);
    }
  }, [isScrolledPast, dimensions, isPlayerVisible]);

  const courseCompleted = (e) => {
    e.preventDefault();
    // Mark the course as completed
    const completeData = new FormData();
    completeData.append('courseId', appState.course.id);
    completeData.append('lessonKey', lesson.slug);
    completeData.append('unitKey', unit.key);
    completeData.append('status', 'COMPLETED');
    axios.post(appState.envVars.COURSE_PROGRESS_API, completeData,
      {
        headers: {
        'X-WP-Nonce': window.ttnonce || window.TTI_REST_NONCE
    }})
      .then(res => {
        if(res) {
          appDispatch({
            type: 'setLessonStatus',
            lessonKey: lesson.slug,
            unitKey: unit.key,
            status: 'COMPLETED'
          })
        }
      })
      .catch(err => {
        console.log(err)
      })
    setIsCompleted(true);
  }

  const goToNextLesson = (e) => {
    e.preventDefault();
    // Go to the next lesson
    const completeData = new FormData();
    completeData.append('courseId', appState.course.id);
    completeData.append('lessonKey', lesson.slug);
    completeData.append('unitKey', unit.key);
    completeData.append('status', 'COMPLETED');
    setIsNextLoading(true)
    axios.post(appState.envVars.COURSE_PROGRESS_API, completeData,
      {
        headers: {
        'X-WP-Nonce': window.ttnonce || window.TTI_REST_NONCE
      }})
      .then(res => {
        if(res) {
          appDispatch({
            type: 'setLessonStatus',
            lessonKey: lesson.slug,
            unitKey: unit.key,
            status: 'COMPLETED'
          })
        }
      })
      .catch(err => {
        console.log(err)
      })
      .finally(() => {
        const startData = new FormData();
        startData.append('courseId', appState.course.id);
        startData.append('lessonKey', next.lesson.slug);
        startData.append('unitKey', next.unit.key);
        startData.append('status', 'STARTED');
        axios.post(appState.envVars.COURSE_PROGRESS_API, startData,
          {
            headers: {
            'X-WP-Nonce': window.ttnonce || window.TTI_REST_NONCE
          }})
          .then(res => {
            if(res) {
              appDispatch({
                type: 'setLessonStatus',
                lessonKey: next.lesson.slug,
                unitKey: next.unit.key,
                status: 'STARTED'
              })
            }
          })
          .catch(err => {
            console.log(err)
          })
          .finally(() => {
            setIsNextLoading(false);
            history.push(`/courses/${appState.course.slug}/${next.unit.key}/${next.lesson.slug}`)
          })
      })
    }

  return (
    <div className="single-lesson-wrapper">
      {lesson && unit ? (
        <>
          <CourseNavigation />
          <LessonVideoSection className="section lesson-video-section">
            <div className="section-content thick-padding no-animation">
              <div className="row two-thirds-width">
                <div className="column white">
                  {lesson.video && (
                    <LessonVideo className="lesson-video" sticky={!isPlayerVisible} playerHeight={playerHeight} ref={wrapperRef}>
                      <div className={`responsive-embed widescreen ${isPlayerVisible ? '' : 'is-sticky'}`} ref={videoRef} dangerouslySetInnerHTML={{__html: lesson.video}} />
                    </LessonVideo>
                  )}
                </div>
              </div>
            </div>
          </LessonVideoSection>
          <LessonContentSection className="section lesson-content-section">
            <div className="section-content normal-padding">
              <div className="row two-thirds">
                <div className="column lesson-content-main">
                  <LessonHeader lesson={lesson} unit={unit} />
                  <LessonTabs lesson={lesson}/>
                  {!isCheckingNext && (
                    <div className={`next-lesson-wrapper`}>
                      {isNextLoading && <div className="tt-loader"></div>}
                      {next ? (
                        <>
                          <h5 className="next-lesson-title">Next Lesson</h5>
                          <div className="next-lesson-link" onClick={goToNextLesson}>
                            {next.lesson.image && (
                              <div className="lesson-image-wrapper">
                                <img src={next.lesson.image} alt={next.lesson.title} />
                              </div>
                            )}
                            <div className="lesson-content-wrapper">
                              <div className="unit-title">{next.unit.unit_name}</div>
                              <h4 className="lesson-title">{next.lesson.title}</h4>
                            </div>
                            <div className="continue-learning-text">Continue Learning <i className="fas fa-arrow-right"></i></div>
                          </div>
                        </>
                      ) : (
                        <div className="completed-message">
                          <Confetti active={isCompleted} config={confettiConfig} />
                          {isCompleted ? (
                            <>
                              <h3 className="completed-title">Congratulations!</h3>
                              <h5 className="completed-subtitle">You have completed the course.</h5>
                              <a href="/courses" className="button return-to-courses-button">View More Courses</a>
                            </>
                          ) : (
                            <div className="button course-complete-button" onClick={courseCompleted}>Course Complete</div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </LessonContentSection>
        </>
      ) : (
        <Loader />
      )}
    </div>
  );
}

const LessonVideoSection = styled.section`
  background: var(--dark);
  color: var(--white);
`
const LessonContentSection = styled.section`
  background: ${({theme}) => theme.background};
  color: ${({theme}) => theme.color};
`

const LessonVideo = styled.div`
  height: ${({sticky, playerHeight}) => sticky ? playerHeight + 'px' : 'auto'};
`